import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle2 } from 'lucide-react';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationsPage,
});

function NotificationsPage(): JSX.Element {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.listNotifications(),
    refetchInterval: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = listQuery.data ?? [];
  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Notifications
          </h1>
          {unreadCount > 0 ? <Badge>{unreadCount} unread</Badge> : null}
        </div>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Assignments and approvals arrive immediately. Everything else waits for the daily digest.
        </p>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="rounded-full bg-[hsl(var(--muted))] p-4 text-[hsl(var(--brand))]">
              <Bell className="size-6" />
            </div>
            <p className="font-medium">You are all caught up.</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              New notifications will appear here in real time.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((n) => (
            <Card key={n.id} className={n.read ? 'opacity-70' : ''}>
              <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{n.subject}</CardTitle>
                    {!n.read ? <Badge variant="default">New</Badge> : null}
                  </div>
                  <CardDescription>
                    {new Date(n.createdAt).toLocaleString()} - {n.kind}
                  </CardDescription>
                </div>
                {!n.read ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => markReadMutation.mutate(n.id)}
                    disabled={markReadMutation.isPending}
                  >
                    <CheckCircle2 className="size-4" />
                    Mark read
                  </Button>
                ) : null}
              </CardHeader>
              <CardContent className="pt-0">
                <p className="whitespace-pre-wrap text-sm text-[hsl(var(--foreground))]">{n.body}</p>
                {n.deepLink ? (
                  <a
                    href={n.deepLink}
                    className="mt-3 inline-flex text-sm font-medium text-[hsl(var(--brand))] hover:underline"
                  >
                    Open item
                  </a>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
